/**
 * EntityPage · Pagafácil Hub (Idea 9 ROADMAP).
 *
 * 3 modos en un mismo archivo · routing decide cuál:
 *   - 'create'  · /entidad/nueva
 *   - 'edit'    · /entidad/:slug/ficha?t=adminToken
 *   - 'lookup'  · /entidades  (introducir email → ver lista entidades)
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../api/client';
import TopBar from '../components/TopBar';
import toast from 'react-hot-toast';


interface JuntaMember {
  name: string;
  role: string;
  email?: string;
  phone?: string;
}

interface EntityView {
  id: string;
  slug: string;
  name: string;
  legal_name?: string | null;
  nif?: string | null;
  contact_email: string;
  contact_phone?: string | null;
  contact_name?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
  entity_type?: string | null;
  junta: JuntaMember[];
  categories: string[];
  logo_url?: string | null;
  description?: string | null;
  admin_token?: string;
  admin_url?: string;
}


const ENTITY_TYPES = [
  { v: 'hermandad', l: 'Hermandad / cofradía' },
  { v: 'ampa', l: 'AMPA' },
  { v: 'club_deportivo', l: 'Club deportivo' },
  { v: 'pena', l: 'Peña' },
  { v: 'asociacion', l: 'Asociación' },
  { v: 'autonomo', l: 'Autónomo · profesional' },
  { v: 'otra', l: 'Otra' },
];

const JUNTA_ROLES = [
  'presidente', 'vicepresidente', 'tesorero', 'secretario',
  'vocal', 'hermano_mayor', 'delegado', 'otro',
];

// Copy adaptativo según entity_type · el Hub se llama distinto para
// autónomos (Oficina Fácil) vs entidades colectivas.
function copyForEntity(entityType: string | null | undefined) {
  const isAutonomo = entityType === 'autonomo';
  return {
    junta:       isAutonomo ? 'Equipo'                       : 'Junta directiva',
    juntaEmpty:  isAutonomo ? 'Aún solo tú · sin equipo'     : 'Aún no hay miembros de la junta',
    colaboradores: isAutonomo ? 'Clientes'                   : 'Colaboradores',
    colaboradoresHelp: isAutonomo
      ? 'CRM de tus clientes · email, teléfono, total facturado, tags por servicio.'
      : 'Patrocinadores, proveedores y donantes · CRM ligero.',
    categories:  isAutonomo ? 'Servicios que ofreces'        : 'Categorías / áreas',
    categoriesHelp: isAutonomo
      ? 'Servicios que prestas · diseño web, mantenimiento, consultoría, fotografía…'
      : 'Para clubes: sub-12, sub-14, sub-16, infantil, juvenil, sénior… Para entidades grandes: administración, social, deportiva.',
    campaigns:   isAutonomo ? 'Proyectos'                    : 'Campañas',
    cobros:      isAutonomo ? 'Facturas y cobros'            : 'Cobros vinculados',
    showJunta:   !isAutonomo,
    showFiscalDashboard: isAutonomo,
  };
}


export default function EntityPage({ mode }: { mode: 'create' | 'edit' | 'lookup' }) {
  if (mode === 'lookup') return <LookupView />;
  if (mode === 'create') return <CreateView />;
  return <EditView />;
}


function LookupView() {
  const [email, setEmail] = useState('');
  const [items, setItems] = useState<EntityView[] | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    try {
      const { data } = await api.post('/public/entities/lookup', { email });
      setItems(data.items);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudieron buscar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Mis entidades">
      <p className="text-[14.5px] text-ink-700 mb-6">
        Introduce el email de contacto que usaste al crear la entidad.
      </p>
      <form onSubmit={submit} className="space-y-3 mb-8">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@email.com"
          className="cc-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
        >
          {loading ? 'Buscando…' : 'Buscar mis entidades'}
        </button>
      </form>
      {items !== null && (
        items.length === 0 ? (
          <p className="text-[13.5px] text-ink-500">
            No hay ninguna entidad con ese email.{' '}
            <Link to="/entidad/nueva" className="text-brand hover:text-brand-700 font-semibold">
              Crear una →
            </Link>
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((e) => (
              <li key={e.slug}>
                <Link
                  to={e.admin_url || '#'}
                  className="block bg-paper border border-line rounded-card p-4 hover:border-brand transition"
                >
                  <div className="font-display font-semibold text-[16px] text-ink">{e.name}</div>
                  {e.entity_type && (
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mt-1">
                      · {ENTITY_TYPES.find((t) => t.v === e.entity_type)?.l || e.entity_type}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )
      )}
    </Shell>
  );
}


function CreateView() {
  const [form, setForm] = useState<Partial<EntityView>>({
    name: '',
    legal_name: '',
    nif: '',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    address: '',
    city: '',
    province: '',
    entity_type: 'hermandad',
    description: '',
    junta: [],
    categories: [],
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.contact_email) {
      toast.error('Falta nombre y email de contacto');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/public/entities/', form);
      toast.success('Entidad creada');
      // Redirige al ficha
      window.location.href = data.admin_url || `/entidad/${data.slug}/ficha?t=${data.admin_token}`;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell title="Crear entidad" subtitle="Hermandad, AMPA, club, asociación · La base del Hub de Pagafácil">
      <EntityForm form={form} setForm={setForm} />
      <button
        onClick={submit}
        disabled={saving}
        className="mt-6 w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
      >
        {saving ? 'Creando…' : 'Crear entidad →'}
      </button>
    </Shell>
  );
}


function EditView() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const t = sp.get('t') || '';
  const [form, setForm] = useState<Partial<EntityView> | null>(null);
  const [cobros, setCobros] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[] | null>(null);
  const [events, setEvents] = useState<any[] | null>(null);
  const [collaborators, setCollaborators] = useState<any[] | null>(null);
  const [documents, setDocuments] = useState<any[] | null>(null);
  const [campaigns, setCampaigns] = useState<any[] | null>(null);
  const [recurringInvoices, setRecurringInvoices] = useState<any[] | null>(null);
  const [saving, setSaving] = useState(false);

  const reloadTasks = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/tasks?t=${t}`)
      .then((r) => setTasks(r.data.items || []))
      .catch(() => setTasks([]));
  };

  const reloadEvents = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/agenda?t=${t}`)
      .then((r) => setEvents(r.data.items || []))
      .catch(() => setEvents([]));
  };

  const reloadCollaborators = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/colaboradores?t=${t}`)
      .then((r) => setCollaborators(r.data.items || []))
      .catch(() => setCollaborators([]));
  };

  const reloadDocuments = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/documentos?t=${t}`)
      .then((r) => setDocuments(r.data.items || []))
      .catch(() => setDocuments([]));
  };

  const reloadCampaigns = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/campanias?t=${t}`)
      .then((r) => setCampaigns(r.data.items || []))
      .catch(() => setCampaigns([]));
  };

  const reloadRecurring = () => {
    if (!slug || !t) return;
    api.get(`/public/entities/admin/${slug}/recurring-invoices?t=${t}`)
      .then((r) => setRecurringInvoices(r.data.items || []))
      .catch(() => setRecurringInvoices([]));
  };

  useEffect(() => {
    if (!slug || !t) return;
    Promise.all([
      api.get(`/public/entities/admin/${slug}?t=${t}`),
      api.get(`/public/entities/admin/${slug}/cobros?t=${t}`),
      api.get(`/public/entities/admin/${slug}/tasks?t=${t}`),
      api.get(`/public/entities/admin/${slug}/agenda?t=${t}`),
      api.get(`/public/entities/admin/${slug}/colaboradores?t=${t}`),
      api.get(`/public/entities/admin/${slug}/documentos?t=${t}`),
      api.get(`/public/entities/admin/${slug}/campanias?t=${t}`),
    ]).then(([eRes, cRes, tRes, evRes, colRes, docRes, campRes]) => {
      setForm(eRes.data);
      setCobros(cRes.data.items || []);
      setTasks(tRes.data.items || []);
      setEvents(evRes.data.items || []);
      setCollaborators(colRes.data.items || []);
      setDocuments(docRes.data.items || []);
      setCampaigns(campRes.data.items || []);
      // Recurrencia · solo si autónomo
      if (eRes.data.entity_type === 'autonomo') {
        api.get(`/public/entities/admin/${slug}/recurring-invoices?t=${t}`)
          .then((r) => setRecurringInvoices(r.data.items || []))
          .catch(() => setRecurringInvoices([]));
      }
    }).catch((err) => {
      toast.error(err.response?.data?.detail || 'No se pudo cargar');
    });
  }, [slug, t]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/public/entities/admin/${slug}?t=${t}`, form);
      setForm(data);
      toast.success('Guardado');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Shell title="Cargando…"><p className="text-ink-500">Un momento.</p></Shell>;

  const copy = copyForEntity(form.entity_type);

  return (
    <Shell
      title={form.name || 'Entidad'}
      subtitle={
        form.entity_type === 'autonomo'
          ? 'Tu oficina · clientes, facturas, agenda fiscal y dashboard'
          : 'Ficha de la entidad · datos legales + junta + categorías'
      }
    >
      <EntityForm form={form} setForm={setForm} />

      {/* Dashboard fiscal · solo para autónomos */}
      {copy.showFiscalDashboard && (
        <>
          <AutonomoDashboard slug={slug || ''} token={t} />
          <RecurringInvoicesPanel
            slug={slug || ''}
            token={t}
            items={recurringInvoices}
            onChange={reloadRecurring}
          />
        </>
      )}

      {cobros.length > 0 && (
        <div className="mt-8 bg-paper border border-line rounded-card p-5">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · {copy.cobros} · {cobros.length}
          </div>
          <ul className="space-y-1.5">
            {cobros.slice(0, 10).map((c) => (
              <li key={c.slug} className="flex items-baseline justify-between text-[13.5px]">
                <a href={c.admin_url} className="text-ink hover:text-brand font-display font-semibold truncate">
                  {c.title}
                </a>
                <span className="text-ink-500 text-[12px] font-mono">{c.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tareas · igual para todos */}
      <TasksPanel
        slug={slug || ''}
        token={t}
        tasks={tasks}
        onChange={reloadTasks}
        juntaNames={(form.junta || []).map((m: any) => m.name).filter(Boolean)}
        cobros={cobros}
      />

      {/* Agenda · con sembrado fiscal AEAT si autónomo (botón en panel) */}
      <AgendaPanel
        slug={slug || ''}
        token={t}
        events={events}
        onChange={reloadEvents}
        showFiscalSeed={form.entity_type === 'autonomo'}
      />

      {/* Colaboradores / Clientes (mismo modelo, distinto copy) */}
      <CollaboratorsPanel
        slug={slug || ''}
        token={t}
        collaborators={collaborators}
        onChange={reloadCollaborators}
        labelOverride={copy.colaboradores}
        helpOverride={copy.colaboradoresHelp}
      />

      {/* Documentos · igual para todos */}
      <DocumentsPanel
        slug={slug || ''}
        token={t}
        documents={documents}
        onChange={reloadDocuments}
      />

      {/* Idea 11 ROADMAP · campañas */}
      <CampaignsPanel
        slug={slug || ''}
        token={t}
        campaigns={campaigns}
        cobros={cobros}
        onChange={reloadCampaigns}
      />

      <button
        onClick={save}
        disabled={saving}
        className="mt-6 w-full px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[15px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
      >
        {saving ? 'Guardando…' : 'Guardar cambios'}
      </button>
    </Shell>
  );
}


function EntityForm({
  form,
  setForm,
}: {
  form: Partial<EntityView>;
  setForm: (v: Partial<EntityView>) => void;
}) {
  const update = (patch: Partial<EntityView>) => setForm({ ...form, ...patch });
  const junta = form.junta || [];
  const categories = form.categories || [];
  const copy = copyForEntity(form.entity_type);

  return (
    <div className="space-y-5">
      {/* Nombre + tipo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="cc-label">Nombre *</label>
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => update({ name: e.target.value })}
            placeholder={form.entity_type === 'autonomo' ? 'Juan Pérez · diseñador web' : 'Hermandad de la Esperanza'}
            className="cc-input"
          />
        </div>
        <div>
          <label className="cc-label">Tipo</label>
          <select
            value={form.entity_type || ''}
            onChange={(e) => update({ entity_type: e.target.value })}
            className="cc-input"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t.v} value={t.v}>{t.l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Datos legales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="cc-label">Razón social</label>
          <input
            type="text"
            value={form.legal_name || ''}
            onChange={(e) => update({ legal_name: e.target.value })}
            placeholder="Hermandad de la Esperanza, S.L. (si aplica)"
            className="cc-input"
          />
        </div>
        <div>
          <label className="cc-label">NIF / CIF</label>
          <input
            type="text"
            value={form.nif || ''}
            onChange={(e) => update({ nif: e.target.value })}
            placeholder="G12345678"
            className="cc-input font-mono"
          />
        </div>
      </div>

      <div>
        <label className="cc-label">Dirección</label>
        <input
          type="text"
          value={form.address || ''}
          onChange={(e) => update({ address: e.target.value })}
          placeholder="Calle, número"
          className="cc-input"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <input
          type="text"
          value={form.postal_code || ''}
          onChange={(e) => update({ postal_code: e.target.value })}
          placeholder="CP"
          className="cc-input"
        />
        <input
          type="text"
          value={form.city || ''}
          onChange={(e) => update({ city: e.target.value })}
          placeholder="Localidad"
          className="cc-input"
        />
        <input
          type="text"
          value={form.province || ''}
          onChange={(e) => update({ province: e.target.value })}
          placeholder="Provincia"
          className="cc-input"
        />
      </div>

      {/* Contacto */}
      <div>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-2 mt-2">
          · Contacto principal
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            value={form.contact_name || ''}
            onChange={(e) => update({ contact_name: e.target.value })}
            placeholder="Nombre del tesorero"
            className="cc-input"
          />
          <input
            type="email"
            value={form.contact_email || ''}
            onChange={(e) => update({ contact_email: e.target.value })}
            placeholder="email@entidad.es *"
            className="cc-input"
          />
          <input
            type="tel"
            value={form.contact_phone || ''}
            onChange={(e) => update({ contact_phone: e.target.value })}
            placeholder="Teléfono"
            className="cc-input"
          />
        </div>
      </div>

      {/* Descripción */}
      <div>
        <label className="cc-label">Descripción</label>
        <textarea
          value={form.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          rows={3}
          placeholder="Breve presentación · qué hace la entidad, cuándo se fundó…"
          className="cc-input"
        />
      </div>

      {/* Junta directiva · oculta para autónomos */}
      {copy.showJunta && (
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label className="cc-label !mb-0">{copy.junta}</label>
          <button
            type="button"
            onClick={() => update({ junta: [...junta, { name: '', role: 'vocal' }] })}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
          >
            + Añadir miembro
          </button>
        </div>
        <ul className="space-y-2">
          {junta.map((m, i) => (
            <li key={i} className="grid grid-cols-[1fr_140px_auto] gap-2">
              <input
                type="text"
                value={m.name}
                onChange={(e) => {
                  const next = [...junta];
                  next[i] = { ...next[i], name: e.target.value };
                  update({ junta: next });
                }}
                placeholder="Nombre"
                className="cc-input"
              />
              <select
                value={m.role}
                onChange={(e) => {
                  const next = [...junta];
                  next[i] = { ...next[i], role: e.target.value };
                  update({ junta: next });
                }}
                className="cc-input"
              >
                {JUNTA_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => update({ junta: junta.filter((_, idx) => idx !== i) })}
                className="text-ink-400 hover:text-brick px-2 text-[18px]"
                aria-label="Quitar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </div>
      )}

      {/* Categorías / Servicios (depende del tipo de entidad) */}
      <div>
        <label className="cc-label">{copy.categories}</label>
        <p className="text-[12px] text-ink-500 mb-2">
          {copy.categoriesHelp}
        </p>
        <input
          type="text"
          value={categories.join(', ')}
          onChange={(e) => update({
            categories: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
          })}
          placeholder={form.entity_type === 'autonomo' ? 'diseño web, mantenimiento, consultoría' : 'sub-12, sub-14, sub-16'}
          className="cc-input"
        />
      </div>

      <Style />
    </div>
  );
}


function TasksPanel({
  slug,
  token,
  tasks,
  onChange,
  juntaNames,
  cobros,
}: {
  slug: string;
  token: string;
  tasks: any[] | null;
  onChange: () => void;
  juntaNames: string[];
  cobros: any[];
}) {
  const [openForm, setOpenForm] = useState(false);
  const [title, setTitle] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [cobroSlug, setCobroSlug] = useState('');
  const [saving, setSaving] = useState(false);

  if (tasks === null) return null;

  const create = async () => {
    if (!title.trim()) {
      toast.error('Pon un título');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/entities/admin/${slug}/tasks?t=${token}`, {
        title: title.trim(),
        assigned_to: assignedTo.trim() || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        group_buy_slug: cobroSlug || null,
      });
      toast.success('Tarea creada');
      setTitle('');
      setAssignedTo('');
      setDueDate('');
      setCobroSlug('');
      setOpenForm(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, newStatus: string) => {
    try {
      await api.patch(`/public/entities/admin/${slug}/tasks/${id}?t=${token}`, { status: newStatus });
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar esta tarea?')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/tasks/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const statusColor: Record<string, { bg: string; text: string }> = {
    open: { bg: '#F7EBD2', text: '#8A5F12' },
    in_progress: { bg: '#E6EEF3', text: '#154B6B' },
    closed: { bg: '#E8EEDC', text: '#46602C' },
  };

  return (
    <div className="mt-8 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Tareas · {tasks.length}
        </div>
        <button
          type="button"
          onClick={() => setOpenForm((v) => !v)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
        >
          {openForm ? '×' : '+ Añadir'}
        </button>
      </div>

      {openForm && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="¿Qué hay que hacer?"
            className="cc-input"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="cc-input"
            >
              <option value="">Asignar a…</option>
              {juntaNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="cc-input"
            />
            <select
              value={cobroSlug}
              onChange={(e) => setCobroSlug(e.target.value)}
              className="cc-input"
            >
              <option value="">Sin cobro</option>
              {cobros.map((c) => (
                <option key={c.slug} value={c.slug}>{c.title}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Guardando…' : 'Crear tarea'}
          </button>
        </div>
      )}

      {tasks.length === 0 ? (
        <p className="text-[13px] text-ink-500">Aún no hay ninguna.</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => {
            const c = statusColor[task.status] || statusColor.open;
            return (
              <li key={task.id} className="border border-line-soft rounded-xl p-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={task.status === 'closed'}
                  onChange={() => setStatus(task.id, task.status === 'closed' ? 'open' : 'closed')}
                  className="mt-1 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className={`font-display font-semibold text-[14.5px] tracking-[-0.01em] ${task.status === 'closed' ? 'text-ink-400 line-through' : 'text-ink'}`}>
                    {task.title}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-[12px] text-ink-500">
                    <span
                      className="px-2 py-0.5 rounded-pill text-[10.5px] font-bold uppercase tracking-wide"
                      style={{ background: c.bg, color: c.text }}
                    >
                      {task.status === 'in_progress' ? 'en curso' : task.status === 'closed' ? 'hecho' : 'abierta'}
                    </span>
                    {task.assigned_to && <span>· {task.assigned_to}</span>}
                    {task.due_date && (
                      <span>· vence {new Date(task.due_date).toLocaleDateString('es-ES')}</span>
                    )}
                    {task.group_buy_slug && (
                      <a href={`/g/${task.group_buy_slug}`} className="text-brand hover:text-brand-700">
                        · cobro
                      </a>
                    )}
                  </div>
                </div>
                {task.status !== 'closed' && task.status !== 'in_progress' && (
                  <button
                    type="button"
                    onClick={() => setStatus(task.id, 'in_progress')}
                    className="text-[12px] text-ink-500 hover:text-brand"
                  >
                    en curso
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(task.id)}
                  className="text-ink-400 hover:text-brick text-[16px]"
                  aria-label="Borrar"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function AgendaPanel({
  slug,
  token,
  events,
  onChange,
  showFiscalSeed = false,
}: {
  slug: string;
  token: string;
  events: any[] | null;
  onChange: () => void;
  showFiscalSeed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [type, setType] = useState('reunion');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const seedFiscal = async () => {
    if (!confirm('¿Inyectar los plazos fiscales AEAT (Modelos 130, 303, 390, 347, 100)? No duplica los que ya tengas.')) return;
    setSeeding(true);
    try {
      const { data } = await api.post(`/public/entities/admin/${slug}/agenda/seed-fiscal?t=${token}`);
      toast.success(`${data.inserted} plazos fiscales añadidos a tu agenda`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo sembrar');
    } finally {
      setSeeding(false);
    }
  };

  if (events === null) return null;

  const create = async () => {
    if (!title.trim() || !startDate) {
      toast.error('Título y fecha obligatorios');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/entities/admin/${slug}/agenda?t=${token}`, {
        title: title.trim(),
        start_date: new Date(startDate).toISOString(),
        all_day: allDay,
        event_type: type,
        location: location.trim() || null,
      });
      toast.success('Evento añadido');
      setTitle('');
      setStartDate('');
      setLocation('');
      setOpen(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar este evento?')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/agenda/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  // Agrupa por mes
  const byMonth: Record<string, any[]> = {};
  events.forEach((ev) => {
    const d = new Date(ev.start_date);
    const key = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    if (!byMonth[key]) byMonth[key] = [];
    byMonth[key].push(ev);
  });

  return (
    <div className="mt-6 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Agenda · {events.length}
        </div>
        <div className="flex items-center gap-3">
          {showFiscalSeed && (
            <button
              type="button"
              onClick={seedFiscal}
              disabled={seeding}
              className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-amberCC-700 hover:text-amberCC disabled:opacity-50"
              title="Inyecta plazos AEAT (Modelos 130, 303, 390, 347, 100)"
            >
              {seeding ? 'Sembrando…' : '+ Plazos AEAT'}
            </button>
          )}
          <a
            href={`/api/public/entities/${slug}/agenda.ics`}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-500 hover:text-brand"
            title="Suscribir en Google/Apple Calendar (URL pública)"
          >
            iCal ↓
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
          >
            {open ? '×' : '+ Añadir'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Reunión de junta · viaje · cierre del cobro…"
            className="cc-input"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type={allDay ? 'date' : 'datetime-local'}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="cc-input"
            />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="cc-input"
            >
              <option value="reunion">Reunión</option>
              <option value="plazo_fiscal">Plazo fiscal</option>
              <option value="evento">Evento</option>
              <option value="fecha_cobro">Cierre de cobro</option>
              <option value="viaje">Viaje</option>
              <option value="otro">Otro</option>
            </select>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ubicación"
              className="cc-input"
            />
          </div>
          <label className="flex items-center gap-2 text-[12.5px] text-ink-700">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
            />
            Todo el día
          </label>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Guardando…' : 'Añadir al calendario'}
          </button>
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-[13px] text-ink-500">Aún no hay eventos.</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(byMonth).map(([month, evs]) => (
            <div key={month}>
              <div className="font-display font-semibold text-[14px] text-ink-700 capitalize mb-2">
                {month}
              </div>
              <ul className="space-y-1.5">
                {evs.map((ev: any) => {
                  const d = new Date(ev.start_date);
                  return (
                    <li key={ev.id} className="flex items-baseline gap-3 text-[13.5px] py-1.5 border-b border-line-soft">
                      <div className="w-12 text-center shrink-0">
                        <div className="font-display font-bold text-[18px] text-ink leading-none">
                          {d.getDate()}
                        </div>
                        <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-500 mt-1">
                          {d.toLocaleDateString('es-ES', { weekday: 'short' })}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-ink tracking-[-0.01em] truncate">
                          {ev.title}
                        </div>
                        <div className="text-[11.5px] text-ink-500 truncate">
                          {!ev.all_day && d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          {ev.location && (ev.all_day ? '' : ' · ') + ev.location}
                          {ev.event_type && ` · ${ev.event_type}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(ev.id)}
                        className="text-ink-400 hover:text-brick text-[16px]"
                        aria-label="Borrar"
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CollaboratorsPanel({
  slug,
  token,
  collaborators,
  onChange,
  labelOverride,
  helpOverride,
}: {
  slug: string;
  token: string;
  collaborators: any[] | null;
  onChange: () => void;
  labelOverride?: string;
  helpOverride?: string;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('patrocinador');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [tags, setTags] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (collaborators === null) return null;

  const create = async () => {
    if (!name.trim()) {
      toast.error('Nombre obligatorio');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/entities/admin/${slug}/colaboradores?t=${token}`, {
        name: name.trim(),
        collaborator_type: type,
        contact_name: contact.trim() || null,
        phone: phone.trim() || null,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
        total_value_eur: parseFloat(value) || 0,
      });
      toast.success('Colaborador añadido');
      setName('');
      setContact('');
      setPhone('');
      setTags('');
      setValue('');
      setOpen(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar este colaborador?')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/colaboradores/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const visible = filter
    ? collaborators.filter((c) => c.collaborator_type === filter)
    : collaborators;

  const types = Array.from(new Set(collaborators.map((c) => c.collaborator_type)));
  const totalAcumulado = collaborators.reduce((s, c) => s + (c.total_value_eur || 0), 0);

  return (
    <div className="mt-6 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · {labelOverride || 'Colaboradores'} · {collaborators.length}
          {totalAcumulado > 0 && (
            <span className="text-olive-700 ml-2">
              · {totalAcumulado.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
        >
          {open ? '×' : '+ Añadir'}
        </button>
      </div>

      {types.length > 1 && (
        <div className="flex gap-2 mb-3 flex-wrap">
          <button
            type="button"
            onClick={() => setFilter('')}
            className={`px-2.5 py-1 rounded-pill text-[10.5px] font-bold uppercase tracking-wide transition ${
              !filter ? 'bg-brand text-paper' : 'bg-surface text-ink-700 hover:bg-brand-50'
            }`}
          >
            todos
          </button>
          {types.map((tp: string) => (
            <button
              key={tp}
              type="button"
              onClick={() => setFilter(filter === tp ? '' : tp)}
              className={`px-2.5 py-1 rounded-pill text-[10.5px] font-bold uppercase tracking-wide transition ${
                filter === tp ? 'bg-brand text-paper' : 'bg-surface text-ink-700 hover:bg-brand-50'
              }`}
            >
              {tp.replace('_', ' ')}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre · empresa o persona"
              className="cc-input"
            />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="cc-input"
            >
              <option value="patrocinador">Patrocinador</option>
              <option value="proveedor">Proveedor</option>
              <option value="donante">Donante</option>
              <option value="contacto">Contacto</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Persona de contacto"
              className="cc-input"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Teléfono"
              className="cc-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Tags · imprenta, papelería, oro…"
              className="cc-input"
            />
            <input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Valor acumulado (€)"
              className="cc-input"
            />
          </div>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Guardando…' : 'Añadir colaborador'}
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-[13px] text-ink-500">Aún no hay ninguno.</p>
      ) : (
        <ul className="divide-y divide-line-soft">
          {visible.map((c: any) => (
            <li key={c.id} className="py-2.5 flex items-baseline gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[14.5px] text-ink tracking-[-0.01em] truncate">
                  {c.name}
                </div>
                <div className="text-[12px] text-ink-500 truncate">
                  {c.collaborator_type.replace('_', ' ')}
                  {c.contact_name && ` · ${c.contact_name}`}
                  {c.phone && ` · ${c.phone}`}
                  {(c.tags || []).length > 0 && ` · ${(c.tags || []).join(', ')}`}
                </div>
              </div>
              {c.total_value_eur > 0 && (
                <div className="font-display font-bold text-[14px] text-olive-700 whitespace-nowrap">
                  {Number(c.total_value_eur).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="text-ink-400 hover:text-brick text-[16px]"
                aria-label="Borrar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function DocumentsPanel({
  slug,
  token,
  documents,
  onChange,
}: {
  slug: string;
  token: string;
  documents: any[] | null;
  onChange: () => void;
}) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);

  if (documents === null) return null;

  const upload = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Máximo 20 MB por archivo');
      return;
    }
    const cat = window.prompt(
      'Categoría · acta, autorizacion, nif, contrato, factura, cuentas, otro',
      'otro',
    ) || 'otro';
    const desc = window.prompt('Descripción (opcional)') || '';
    const tags = window.prompt('Tags separados por coma (opcional)') || '';
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const params = new URLSearchParams({
        t: token,
        category: cat,
        description: desc,
        tags_csv: tags,
      });
      await api.post(
        `/public/entities/admin/${slug}/documentos?${params.toString()}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Documento subido');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo subir');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar este documento?')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/documentos/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const visible = documents.filter((d) => {
    if (category && d.category !== category) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (d.filename || '').toLowerCase().includes(s)
        || (d.description || '').toLowerCase().includes(s)
        || (d.tags || []).some((tag: string) => tag.toLowerCase().includes(s))
      );
    }
    return true;
  });

  const categories = Array.from(new Set(documents.map((d) => d.category).filter(Boolean)));
  const totalSize = documents.reduce((s, d) => s + (d.size_bytes || 0), 0);

  return (
    <div className="mt-6 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Documentos · {documents.length}
          {totalSize > 0 && (
            <span className="text-ink-500 ml-2">
              · {(totalSize / 1024 / 1024).toFixed(1)} MB
            </span>
          )}
        </div>
        <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700 cursor-pointer">
          {uploading ? 'Subiendo…' : '+ Subir'}
          <input
            type="file"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>

      {documents.length > 0 && (
        <div className="flex gap-2 mb-3 flex-wrap items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar…"
            className="cc-input !py-1.5 !text-[12.5px] flex-1 min-w-[140px]"
          />
          {categories.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setCategory('')}
                className={`px-2.5 py-1 rounded-pill text-[10.5px] font-bold uppercase tracking-wide transition ${
                  !category ? 'bg-brand text-paper' : 'bg-surface text-ink-700'
                }`}
              >
                todas
              </button>
              {categories.map((c: any) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(category === c ? '' : c)}
                  className={`px-2.5 py-1 rounded-pill text-[10.5px] font-bold uppercase tracking-wide transition ${
                    category === c ? 'bg-brand text-paper' : 'bg-surface text-ink-700'
                  }`}
                >
                  {c}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-[13px] text-ink-500">
          {documents.length === 0
            ? 'Sube actas, autorizaciones, NIFs, justificantes…'
            : 'Sin resultados con esos filtros.'}
        </p>
      ) : (
        <ul className="divide-y divide-line-soft">
          {visible.map((d: any) => (
            <li key={d.id} className="py-2.5 flex items-baseline gap-3">
              <div className="flex-1 min-w-0">
                <a
                  href={d.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-display font-semibold text-[14px] text-ink hover:text-brand tracking-[-0.01em] truncate block"
                >
                  {d.filename}
                </a>
                <div className="text-[11.5px] text-ink-500 truncate">
                  {d.category && <span className="font-mono uppercase tracking-wide mr-2">{d.category}</span>}
                  {(d.size_bytes / 1024).toFixed(0)} KB
                  {d.description && ` · ${d.description}`}
                  {(d.tags || []).length > 0 && ` · ${(d.tags || []).join(', ')}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(d.id)}
                className="text-ink-400 hover:text-brick text-[16px]"
                aria-label="Borrar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function CampaignsPanel({
  slug,
  token,
  campaigns,
  cobros,
  onChange,
}: {
  slug: string;
  token: string;
  campaigns: any[] | null;
  cobros: any[];
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [season, setSeason] = useState('');
  const [saving, setSaving] = useState(false);

  if (campaigns === null) return null;

  const create = async () => {
    if (!name.trim()) {
      toast.error('Nombre obligatorio');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/entities/admin/${slug}/campanias?t=${token}`, {
        name: name.trim(),
        season: season.trim() || null,
      });
      toast.success('Campaña creada');
      setName('');
      setSeason('');
      setOpen(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar la campaña? Los cobros vinculados quedarán sin campaña pero NO se borran.')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/campanias/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const linkCobro = async (campaignId: string | null, cobroSlug: string) => {
    try {
      await api.post(`/public/entities/admin/${slug}/campanias/link?t=${token}`, {
        cobro_slug: cobroSlug,
        campaign_id: campaignId,
      });
      toast.success(campaignId ? 'Vinculado' : 'Desvinculado');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo vincular');
    }
  };

  // Asocia cobros por campaign_id (vienen del listado /cobros de la entidad)
  // Para detectar campaign_id necesitamos enriquecer cobros; como /cobros no
  // expone campaign_id ahora, hacemos lookup naive por título y dejamos UI
  // simple: por cada campaña, lista vacía + dropdown de "añadir cobro" que
  // mete cualquiera. Click en cobro mete una × para quitar (que en próximo
  // refresh aparece donde toca según backend).

  return (
    <div className="mt-6 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Campañas · {campaigns.length}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
        >
          {open ? '×' : '+ Crear'}
        </button>
      </div>

      {open && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Temporada Bravo Murillo · Cuaresma 2027 · etc"
              className="cc-input"
            />
            <input
              type="text"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              placeholder="Temporada (opcional · 2026/27)"
              className="cc-input"
            />
          </div>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Guardando…' : 'Crear campaña'}
          </button>
        </div>
      )}

      {campaigns.length === 0 ? (
        <p className="text-[13px] text-ink-500">
          Aún no hay ninguna. Agrupa cobros bajo una temporada o un proyecto para verlos juntos.
        </p>
      ) : (
        <ul className="space-y-3">
          {campaigns.map((camp: any) => (
            <li key={camp.id} className="border border-line-soft rounded-xl p-3">
              <div className="flex items-baseline justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[15px] text-ink tracking-[-0.01em] truncate">
                    {camp.name}
                  </div>
                  <div className="text-[12px] text-ink-500">
                    {camp.season && `${camp.season} · `}
                    {camp.cobros_count} cobros · {camp.total_paid_eur.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(camp.id)}
                  className="text-ink-400 hover:text-brick text-[16px]"
                  aria-label="Borrar"
                >
                  ×
                </button>
              </div>
              {cobros.length > 0 && (
                <details className="mt-1">
                  <summary className="text-[11.5px] text-brand hover:text-brand-700 cursor-pointer font-mono uppercase tracking-wide">
                    + Añadir cobro a esta campaña
                  </summary>
                  <div className="mt-2 max-h-[160px] overflow-y-auto space-y-1">
                    {cobros.map((c: any) => (
                      <button
                        key={c.slug}
                        type="button"
                        onClick={() => linkCobro(camp.id, c.slug)}
                        className="w-full text-left px-3 py-1.5 text-[12.5px] rounded border border-line bg-paper hover:border-brand transition"
                      >
                        {c.title}
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function RecurringInvoicesPanel({
  slug,
  token,
  items,
  onChange,
}: {
  slug: string;
  token: string;
  items: any[] | null;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientNif, setClientNif] = useState('');
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [periodicity, setPeriodicity] = useState('monthly');
  const [dayOfPeriod, setDayOfPeriod] = useState('1');
  const [saving, setSaving] = useState(false);

  if (items === null) return null;

  const create = async () => {
    if (!clientName.trim() || !clientEmail.trim() || !amount || !concept.trim()) {
      toast.error('Cliente, email, importe y concepto son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/public/entities/admin/${slug}/recurring-invoices?t=${token}`, {
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        client_nif: clientNif.trim() || null,
        amount_eur: parseFloat(amount),
        concept: concept.trim(),
        periodicity,
        day_of_period: parseInt(dayOfPeriod, 10) || 1,
      });
      toast.success('Recurrencia activada');
      setClientName('');
      setClientEmail('');
      setClientNif('');
      setAmount('');
      setConcept('');
      setOpen(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo crear');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (ri: any) => {
    try {
      await api.patch(`/public/entities/admin/${slug}/recurring-invoices/${ri.id}?t=${token}`, {
        active: !ri.active,
      });
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Borrar esta recurrencia? Las facturas ya generadas se conservan.')) return;
    try {
      await api.delete(`/public/entities/admin/${slug}/recurring-invoices/${id}?t=${token}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const runNow = async (id: string) => {
    if (!confirm('¿Emitir factura ahora? Avanza la próxima ejecución según periodicidad.')) return;
    try {
      const { data } = await api.post(`/public/entities/admin/${slug}/recurring-invoices/${id}/run-now?t=${token}`);
      toast.success(`Factura emitida · ${data.order_reference}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo emitir');
    }
  };

  const PERIODICITY_LABEL: Record<string, string> = {
    monthly: 'mensual',
    quarterly: 'trimestral',
    annual: 'anual',
    biweekly: 'quincenal',
  };

  const totalBilled = items.reduce((s, r) => s + (r.total_billed_eur || 0), 0);
  const activeCount = items.filter((r) => r.active).length;

  return (
    <div className="mt-6 bg-paper border border-line rounded-card p-5">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
          · Facturas recurrentes · {activeCount}/{items.length} activas
          {totalBilled > 0 && (
            <span className="text-olive-700 ml-2">
              · facturado {totalBilled.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
        >
          {open ? '×' : '+ Nueva'}
        </button>
      </div>

      {open && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Nombre del cliente"
              className="cc-input"
            />
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="email@cliente.es"
              className="cc-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={clientNif}
              onChange={(e) => setClientNif(e.target.value)}
              placeholder="NIF del cliente (opcional)"
              className="cc-input font-mono"
            />
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Importe €"
              className="cc-input"
            />
          </div>
          <input
            type="text"
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            placeholder="Concepto · Mantenimiento mensual web · Consultoría seguridad …"
            className="cc-input"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select
              value={periodicity}
              onChange={(e) => setPeriodicity(e.target.value)}
              className="cc-input"
            >
              <option value="monthly">Mensual</option>
              <option value="biweekly">Quincenal</option>
              <option value="quarterly">Trimestral</option>
              <option value="annual">Anual</option>
            </select>
            <input
              type="number"
              min="1"
              max="28"
              value={dayOfPeriod}
              onChange={(e) => setDayOfPeriod(e.target.value)}
              placeholder="Día (1-28)"
              className="cc-input"
            />
          </div>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Activando…' : 'Activar recurrencia'}
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-[13px] text-ink-500">
          Aún sin recurrencias · activa la primera para emitir facturas automáticas a tus clientes habituales.
        </p>
      ) : (
        <ul className="divide-y divide-line-soft">
          {items.map((ri: any) => {
            const next = ri.next_run_at ? new Date(ri.next_run_at) : null;
            const daysUntil = next ? Math.round((next.getTime() - Date.now()) / 86400000) : null;
            return (
              <li key={ri.id} className="py-3">
                <div className="flex items-baseline gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[14.5px] text-ink truncate">
                      {ri.client_name} · {ri.concept}
                    </div>
                    <div className="text-[12px] text-ink-500 truncate">
                      {PERIODICITY_LABEL[ri.periodicity] || ri.periodicity}
                      {' · día '}
                      {ri.day_of_period}
                      {' · '}
                      {ri.total_runs} emitidas
                      {' · '}
                      {Number(ri.total_billed_eur).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-bold text-[15px] text-olive-700 tabular-nums">
                      {Number(ri.amount_eur).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </div>
                    {ri.active && next && (
                      <div className="text-[11px] text-ink-500">
                        próx. {next.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                        {daysUntil !== null && daysUntil >= 0 && ` · en ${daysUntil} d`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => runNow(ri.id)}
                    className="px-2.5 py-1 rounded-pill text-[11px] font-bold uppercase border bg-paper text-brand border-[#CBD9E2] hover:bg-brand hover:text-paper transition"
                  >
                    Emitir ahora
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(ri)}
                    className={`px-2.5 py-1 rounded-pill text-[11px] font-bold uppercase border transition ${
                      ri.active
                        ? 'bg-olive-50 text-olive-700 border-[#C7D5A8]'
                        : 'bg-paper text-ink-500 border-line-strong'
                    }`}
                  >
                    {ri.active ? 'activa' : 'pausada'}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(ri.id)}
                    className="text-ink-400 hover:text-brick text-[16px] ml-auto"
                    aria-label="Borrar"
                  >
                    ×
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function AutonomoDashboard({ slug, token }: { slug: string; token: string }) {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!slug || !token) return;
    api.get(`/public/entities/admin/${slug}/dashboard-autonomo?t=${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [slug, token]);

  if (!data) return null;

  return (
    <div className="mt-8 space-y-4">
      {/* KPIs fiscales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Facturado total" value={`${data.facturado_total_eur.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`} accent="olive" />
        <KpiCard label={data.trimestre_label || 'Trimestre actual'} value={`${data.trimestre_actual_eur.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`} accent="brand" />
        <KpiCard label="IVA repercutido estimado" value={`${data.iva_repercutido_estimado_eur.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`} accent="terra" />
        <KpiCard label="Clientes únicos" value={`${data.clientes_unicos} · ${data.facturas_count} facturas`} accent="ink" />
      </div>

      {/* Próximos plazos fiscales */}
      {data.proximos_plazos && data.proximos_plazos.length > 0 && (
        <div className="bg-amberCC-50 border border-[#ECD9AE] rounded-card p-5">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-amberCC-700 mb-3">
            · Próximos plazos AEAT
          </div>
          <ul className="space-y-1.5">
            {data.proximos_plazos.map((p: any, i: number) => (
              <li key={i} className="flex items-baseline justify-between text-[13px]">
                <span className="font-display font-semibold text-ink truncate">{p.title}</span>
                <span className="text-ink-500 text-[12px] whitespace-nowrap ml-3">
                  {new Date(p.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                  {p.days_until >= 0 && ` · en ${p.days_until} d`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top clientes */}
      {data.top_clientes && data.top_clientes.length > 0 && (
        <div className="bg-paper border border-line rounded-card p-5">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Mejores clientes · top {data.top_clientes.length}
          </div>
          <ul className="divide-y divide-line-soft">
            {data.top_clientes.map((c: any, i: number) => (
              <li key={i} className="py-2 flex items-baseline gap-3 text-[13px]">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-ink truncate">{c.name || c.email}</div>
                  <div className="text-[11.5px] text-ink-500 truncate">{c.email} · {c.facturas} factura{c.facturas === 1 ? '' : 's'}</div>
                </div>
                <span className="font-display font-bold text-olive-700 tabular-nums">
                  {c.total_eur.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[11.5px] text-ink-500 font-mono">
        · IVA y IRPF son estimaciones · no sustituyen a tu declaración real ante AEAT.
      </p>
    </div>
  );
}


function KpiCard({ label, value, accent }: { label: string; value: string; accent: 'olive' | 'brand' | 'terra' | 'ink' }) {
  const color = accent === 'olive' ? 'text-olive-700'
              : accent === 'brand' ? 'text-brand-700'
              : accent === 'terra' ? 'text-terra-700'
              : 'text-ink';
  return (
    <div className="bg-paper border border-line rounded-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
        · {label}
      </div>
      <div className={`font-display font-bold text-[18px] tabular-nums tracking-[-0.01em] ${color}`}>
        {value}
      </div>
    </div>
  );
}


function Shell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel={title} rightCta={{ label: 'Mis cobros', to: '/mis-cobros' }} />
      <header className="bg-paper border-b border-line">
        <div className="max-w-3xl mx-auto px-6 py-7">
          <h1 className="font-display font-bold text-[32px] text-ink tracking-[-0.02em]">
            {title}
          </h1>
          {subtitle && <p className="text-[13.5px] text-ink-500 mt-1">{subtitle}</p>}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
      .cc-label{
        display:block;
        font-family:"Hanken Grotesk",sans-serif;
        font-size:12.5px;
        font-weight:700;
        letter-spacing:.02em;
        color:#4A4239;
        margin-bottom:7px;
      }
    `}</style>
  );
}
